/**
 * ContractHistory slice
 * Unit に紐づく契約履歴を BE の UnitIndex GSI 経由で取得する。
 * 無限スクロール用に cursor / hasMore を保持し、追加 fetch は data に append する。
 */

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ipponApi from "../config/axios";

const BaseURL = import.meta.env.VITE_API_URL;

const DEFAULT_PAGE_SIZE = 10;

export interface ContractHistoryRecord {
  client_id: string;
  sort_key: string;
  unit_id: string;
  contract_id?: string;
  unit_area_type_snapshot?: string;
  landmark_name_snapshot?: string;
  contractor_id?: string;
  contractor_type?: "individual" | "corporate";
  contract_start_date?: string;
  contract_end_date?: string;
  monthly_rent?: string | number;
  rent_unit_price?: string | number;
  maintenance_fee?: string | number;
  maintenance_fee_unit_price?: string | number;
  pricing_type?: "fixed" | "unit_quantity";
  unit_price?: string | number;
  quantity?: string | number;
  quantity_unit?: string;
  security_deposit?: string | number;
  key_money?: string | number;
  contract_remarks?: string;
  contract_form?: string;
  cancellation_date?: string;
  contract_status?: string;
  update_type?: string;
  departure_date?: string;
  departure_reason?: string;
  created_at?: string;
  last_updated_at?: string;
  created_by?: string;
  created_by_name?: string;
  updated_by?: string;
  updated_by_name?: string;
  corrected?: boolean;
  revision_history?: any[];
  contractor_snapshot?: {
    individual_details?: any;
    corporate_details?: any;
  };
}

interface ContractHistoryUnitState {
  data: ContractHistoryRecord[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  cursor?: string;
  hasMore: boolean;
}

interface ContractHistoryState {
  byUnit: Record<string, ContractHistoryUnitState>;
}

const initialState: ContractHistoryState = { byUnit: {} };

const initUnit = (): ContractHistoryUnitState => ({
  data: [],
  loading: false,
  loadingMore: false,
  error: null,
  cursor: undefined,
  hasMore: false,
});

/** 初回取得 — data を置き換え、cursor をリセット */
export const fetchContractHistoryByUnit = createAsyncThunk<
  {
    unit_id: string;
    items: ContractHistoryRecord[];
    nextCursor?: string;
    hasMore: boolean;
  },
  { unit_id: string; limit?: number }
>(
  "contractHistory/fetchByUnit",
  async ({ unit_id, limit = DEFAULT_PAGE_SIZE }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.get(`${BaseURL}/api/v1/contract-history`, {
        params: { unit_id, limit },
      });
      const data = res.data?.data || {};
      return {
        unit_id,
        items: (data.items || []) as ContractHistoryRecord[],
        nextCursor: data.nextCursor as string | undefined,
        hasMore: !!data.hasMore,
      };
    } catch {
      return rejectWithValue("契約履歴の取得に失敗しました");
    }
  },
);

/** 追加取得 — data に append、cursor を更新 */
export const fetchMoreContractHistoryByUnit = createAsyncThunk<
  {
    unit_id: string;
    items: ContractHistoryRecord[];
    nextCursor?: string;
    hasMore: boolean;
  },
  { unit_id: string; limit?: number }
>(
  "contractHistory/fetchMoreByUnit",
  async (
    { unit_id, limit = DEFAULT_PAGE_SIZE },
    { rejectWithValue, getState },
  ) => {
    const state = getState() as { contractHistory: ContractHistoryState };
    const cursor = state.contractHistory.byUnit[unit_id]?.cursor;
    if (!cursor) {
      return { unit_id, items: [], nextCursor: undefined, hasMore: false };
    }
    try {
      const res = await ipponApi.get(`${BaseURL}/api/v1/contract-history`, {
        params: { unit_id, limit, cursor },
      });
      const data = res.data?.data || {};
      return {
        unit_id,
        items: (data.items || []) as ContractHistoryRecord[],
        nextCursor: data.nextCursor as string | undefined,
        hasMore: !!data.hasMore,
      };
    } catch {
      return rejectWithValue("契約履歴の追加取得に失敗しました");
    }
  },
);

const contractHistorySlice = createSlice({
  name: "contractHistory",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchContractHistoryByUnit.pending, (state, action) => {
        const uid = action.meta.arg.unit_id;
        state.byUnit[uid] = state.byUnit[uid] || initUnit();
        state.byUnit[uid].loading = true;
        state.byUnit[uid].error = null;
      })
      .addCase(fetchContractHistoryByUnit.fulfilled, (state, action) => {
        const { unit_id, items, nextCursor, hasMore } = action.payload;
        state.byUnit[unit_id] = {
          data: items,
          loading: false,
          loadingMore: false,
          error: null,
          cursor: nextCursor,
          hasMore,
        };
      })
      .addCase(fetchContractHistoryByUnit.rejected, (state, action) => {
        const uid = action.meta.arg.unit_id;
        state.byUnit[uid] = {
          ...initUnit(),
          error: action.payload as string,
        };
      })
      .addCase(fetchMoreContractHistoryByUnit.pending, (state, action) => {
        const uid = action.meta.arg.unit_id;
        state.byUnit[uid] = state.byUnit[uid] || initUnit();
        state.byUnit[uid].loadingMore = true;
        state.byUnit[uid].error = null;
      })
      .addCase(fetchMoreContractHistoryByUnit.fulfilled, (state, action) => {
        const { unit_id, items, nextCursor, hasMore } = action.payload;
        const cur = state.byUnit[unit_id] || initUnit();
        state.byUnit[unit_id] = {
          ...cur,
          data: [...cur.data, ...items],
          loadingMore: false,
          error: null,
          cursor: nextCursor,
          hasMore,
        };
      })
      .addCase(fetchMoreContractHistoryByUnit.rejected, (state, action) => {
        const uid = action.meta.arg.unit_id;
        const cur = state.byUnit[uid] || initUnit();
        state.byUnit[uid] = {
          ...cur,
          loadingMore: false,
          error: action.payload as string,
        };
      });
  },
});

export default contractHistorySlice.reducer;
